import { FC, ReactNode, useContext, useState } from "react";
import { Button, Space, Typography, Upload, message } from "antd";
import { DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import { RcFile } from "antd/lib/upload";
import { useCreate } from "@refinedev/core";
import { v4 as randomId } from "uuid";

import { errorNotification, successNotification } from "../../../helpers";
import { EnvContext } from "../../../providers";

type Props = {
  value?: string | null;
  onChange?: (value?: string | null) => void;
  /** подсказка под кнопками; по умолчанию — для фона слайда витрины */
  hint?: ReactNode;
};

/** Лимит загрузки CMS (spring.servlet.multipart.max-file-size). */
const MAX_MB = 25;
const TYPES = ["video/mp4", "video/webm"];

/**
 * Фоновое видео слайда: короткий ролик mp4/webm, грузится в папку tv хранилища, в форме — путь.
 * На сайте играет без звука по кругу поверх обложки.
 */
export const TvVideoField: FC<Props> = ({ value, onChange, hint }) => {
  const { mutate } = useCreate();
  const { THUMBOR_URL } = useContext(EnvContext);
  const [uploading, setUploading] = useState(false);
  const src = value
    ? value.startsWith("http")
      ? value
      : `${THUMBOR_URL}${value}`
    : null;

  const beforeUpload = (file: RcFile) => {
    if (!TYPES.includes(file.type)) {
      message.error("Нужен файл mp4 или webm");
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      message.error(`Файл больше ${MAX_MB} МБ — сожмите ролик`);
      return Upload.LIST_IGNORE;
    }
    setUploading(true);
    mutate({
      dataProviderName: "uploadDataProvider",
      resource: "",
      values: {
        entityId: randomId(),
        serviceType: "TV",
        blob: file,
        type: "TV",
      },
      ...errorNotification({
        errorData: { callback: () => setUploading(false) },
      }),
      ...successNotification({
        callback: (data: { s3id: string }) => {
          setUploading(false);
          if (data?.s3id) onChange?.(data.s3id);
        },
        successData: { message: "Видео загружено", type: "success" },
      }),
    });
    return false;
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      {src && (
        <video
          src={src}
          muted
          loop
          autoPlay
          playsInline
          controls
          style={{
            width: "100%",
            maxWidth: 480,
            borderRadius: 8,
            background: "#000",
          }}
        />
      )}
      <Space>
        <Upload
          accept="video/mp4,video/webm"
          showUploadList={false}
          beforeUpload={beforeUpload}
          disabled={uploading}
        >
          <Button icon={<UploadOutlined />} loading={uploading}>
            {value ? "Заменить видео" : "Загрузить видео"}
          </Button>
        </Upload>
        {value && (
          <Button
            icon={<DeleteOutlined />}
            danger
            onClick={() => onChange?.(null)}
          >
            Убрать
          </Button>
        )}
      </Space>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {hint ?? (
          <>
            mp4 или webm до {MAX_MB} МБ. Лучше 10–20 секунд, 1280–1920 px, без
            звука и до 10 МБ — так быстрее грузится на телефоне. Обложка
            остаётся: она видна, пока видео грузится, и если видео не
            запустилось.
          </>
        )}
      </Typography.Text>
    </Space>
  );
};
